import React, { useEffect, useState } from 'react';
import {
  addCodecs,
  bulkAddCodecs,
  createAdhocMeeting,
  createCalendarEntries,
  createClassMeetings,
  deleteMeetings,
  enableGuestSpeaker,
  isAddCodecsAllowed,
  removeCodecs,
  switchToPersonalZoom,
  switchToWebEx,
} from '../utils';
import { useSelector } from 'react-redux';
import PortalDelete from '../../common/PortalDelete';
import PortalConfirmation from '../../common/PortalConfirmation';
import { toast } from 'react-toastify';
import { DL_COURSE_TYPE_Z } from '../../common/constants';
import { getUserEmail, getUserMidas } from '../../MyCourses/CCT/Body/Zoom/ScheduleMeeting/utils';
import Loader from '../../common/Loader';
import DeleteModal from './DeleteModal';
import PersonalZoomLinkModal from './PersonalZoomLinkModal';
import AddCodecsModal from './AddCodecsModal';
import RemoveCodecsModal from './RemoveCodecsModal';
import { AdhocMeetingModal } from './AdhocMeetingModal';

export default function ActionDialogs({
  id,
  selectedCourse,
  selectionModel = {},
  setSelectedCourse,
  setCourseActionMenu,
  setBulkActionMenu,
  codecs = [],
  loadCourses,
  loadCourse,
  loadMeetings,
  futureMeetings = [],
  isCourseListActionsMenu = false,
  isDetailedCourseActionsMenu = false,
  isSmall = false,
}) {
  const impersonation = useSelector((state) => state.impersonationReducer.impersonation);
  const trueUser = useSelector((state) => state.AWSReducer.user);
  const [isClassMeetingsModalOpen, setIsClassMeetingsModalOpen] = useState(false);
  const [isDeleteMeetingsModalOpen, setIsDeleteMeetingsModalOpen] = useState(false);
  const [deleteMeetingsPeriod, setDeleteMeetingsPeriod] = useState('all');
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isPersonalZoomLinkModalOpen, setIsPersonalZoomLinkModalOpen] = useState(false);
  const [isWebExConfirmationModalOpen, setIsWebExConfirmationModalOpen] = useState(false);
  const [isAddCodecsModalOpen, setIsAddCodecsModalOpen] = useState(false);
  const [isRemoveCodecsModalOpen, setIsRemoveCodecsModalOpen] = useState(false);
  const [isCalendarEntriesModalOpen, setIsCalendarEntriesModalOpen] = useState(false);
  const [isAdhocMeetingModalOpen, setIsAdhocMeetingModalOpen] = useState(false);
  const [isEnableGuestSpeakerModalOpen, setIsEnableGuestSpeakerModalOpen] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);

  useEffect(() => {
    if (setBulkActionMenu) {
      setBulkActionMenu({
        createClassMeetings: toggleIsClassMeetingsModalOpen,
        deleteCourseMeetings: toggleIsDeleteMeetingsModalOpen,
        switchToPersonalZoom: toggleIsPersonalZoomLinkModalOpen,
        switchToWebEx: toggleIsWebExConfirmationModalOpen,
        addCodecs: onBulkAddCodecs,
      });
    }
    const courseListMenu = {
      isCourseListActionsMenu,
      isDetailedCourseActionsMenu,
      createClassMeetings: onCreateClassMeetings,
      deleteCourseMeetings: onDelete,
      switchToPersonalZoom: onSwitchToPersonalZoomLink,
      switchToWebEx: onSwitchToWebEx,
      addCodecs: onAddCodecs,
      removeCodecs: onRemoveCodecs,
      createCalendarEntries: onCreateCalendarEntries,
      createAdhocMeeting: onCreateAdhocMeeting,
    };
    if (isDetailedCourseActionsMenu) {
      courseListMenu.enableGuest = onEnableGuestSpeaker;
    }
    setCourseActionMenu(courseListMenu);
  }, []);

  const setCourse = (course) => {
    if (setSelectedCourse) {
      setSelectedCourse(course);
    }
  };

  // Reset selected course and period value when the delete dialogs are closed
  useEffect(() => {
    if (!isDeleteMeetingsModalOpen && !isDeleteConfirmationModalOpen) {
      setDeleteMeetingsPeriod('all');
    }
  }, [isDeleteMeetingsModalOpen, isDeleteConfirmationModalOpen]);

  const toggleIsInProgress = () => {
    setIsInProgress((prev) => !prev);
  };

  const toggleIsClassMeetingsModalOpen = () => {
    setIsClassMeetingsModalOpen((prev) => !prev);
  };

  const onCreateClassMeetings = (course) => {
    setCourse(course);
    toggleIsClassMeetingsModalOpen();
  };

  // Create course meetings
  const onCreateClassMeetingsConfirm = async () => {
    onBulkCreateClassMeetings(selectedCourse ? [selectedCourse] : Object.values(selectionModel));
  };

  // Bulk action - Create course meetings
  const onBulkCreateClassMeetings = async (courses) => {
    try {
      const requests = [];
      toggleIsInProgress();
      const email = getUserEmail(trueUser, impersonation);
      const midas = getUserMidas(trueUser, impersonation);
      courses.map((course) => {
        if (course.TYPE !== DL_COURSE_TYPE_Z) {
          toast.error(
            `The course ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN} is not of type 'z'. Cannot create class meetings.`,
          );
          return;
        } else {
          requests.push(
            createClassMeetings({
              course,
              email,
              midas,
            })
              .then((result) => {
                if (!result)
                  toast.error(
                    `Unable to create class meetings for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
                  );
                toast.success(
                  `Meetings created for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
                );
              })
              .catch((error) => {
                console.log(error);
                toast.error(
                  `Unable to create class meetings for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
                );
              }),
          );
        }
      });

      await Promise.all(requests);
      toggleIsClassMeetingsModalOpen();
      setCourse(null);

      // If the user is on course details page, refresh the page after creating the meetings
      if (isDetailedCourseActionsMenu) {
        loadMeetings();
      }
    } catch (err) {
      console.log(err);
      toast.error('Unable to create class meetings for the course(s).');
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsDeleteMeetingsModalOpen = () => {
    setIsDeleteMeetingsModalOpen((prev) => !prev);
  };

  const toggleIsDeleteConfirmationModalOpen = () => {
    setIsDeleteConfirmationModalOpen((prev) => !prev);
  };

  const onDelete = (course) => {
    setCourse(course);
    toggleIsDeleteMeetingsModalOpen();
  };

  const onDeleteConfirm = () => {
    toggleIsDeleteMeetingsModalOpen();
    toggleIsDeleteConfirmationModalOpen();
  };

  // Delete meetings
  const onDeleteMeetings = async () => {
    onBulkDeleteMeetings(selectedCourse ? [selectedCourse] : Object.values(selectionModel));
  };

  // Bulk action - Delete meetings
  const onBulkDeleteMeetings = async (courses) => {
    try {
      toggleIsInProgress();
      await Promise.all(
        courses.map((course) =>
          deleteMeetings({
            course,
            period: deleteMeetingsPeriod,
          })
            .then((result) => {
              if (!result) {
                toast.error(
                  `Unable to delete meetings for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
                );
              }
              toast.success(
                `Meetings deleted for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
              );
            })
            .catch((error) => {
              console.log(error);
              toast.error(
                `Unable to delete meetings for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
              );
            }),
        ),
      );
      toggleIsDeleteConfirmationModalOpen();
      setCourse(null);
      if (isDetailedCourseActionsMenu) {
        loadMeetings();
      }
    } catch (err) {
      console.log(err);
      toast.error('Unable to delete meetings for the course(s).');
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsPersonalZoomLinkModalOpen = () => {
    setIsPersonalZoomLinkModalOpen((prev) => !prev);
  };

  const onSwitchToPersonalZoomLink = (course) => {
    setCourse(course);
    toggleIsPersonalZoomLinkModalOpen();
  };

  // Switch to personal zoom link
  const onSwitchToPersonalZoomLinkConfirm = async (personalZoomLink) => {
    onBulkSwitchToPersonalZoomLink(
      selectedCourse ? [selectedCourse] : Object.values(selectionModel),
      personalZoomLink,
    );
  };

  //  Bulk action - Switch to personal zoom link
  const onBulkSwitchToPersonalZoomLink = async (courses, personalZoomLink) => {
    try {
      toggleIsInProgress();
      await Promise.all(
        courses.map((course) =>
          switchToPersonalZoom({
            course,
            link: personalZoomLink,
          })
            .then((result) => {
              toast.success(
                `Switched to personal Zoom link for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
              );
            })
            .catch((error) => {
              console.log(error);
              toast.error(
                `Unable to switch to personal Zoom link for ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN}.`,
              );
            }),
        ),
      );
      toggleIsPersonalZoomLinkModalOpen();
      setCourse(null);
    } catch (err) {
      console.log(err);
      toast.error('Unable to switch to personal Zoom link for the course(s).');
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsWebExConfirmationModalOpen = () => {
    setIsWebExConfirmationModalOpen((prev) => !prev);
  };

  const onSwitchToWebEx = (course) => {
    setCourse(course);
    toggleIsWebExConfirmationModalOpen();
  };

  // Switch to WebEx
  const onSwitchToWebExConfirm = async (course) => {
    onBulkSwitchToWebEx(selectedCourse ? [selectedCourse] : Object.values(selectionModel));
  };

  //  Bulk action - Switch to WebEx
  const onBulkSwitchToWebEx = async (courses) => {
    try {
      toggleIsInProgress();
      await Promise.all(
        courses.map((course) =>
          switchToWebEx(course)
            .then((result) => {
              toast.success(`Switched meetings to WebEx for ${course.CRN}.`);
            })
            .catch((error) => {
              console.log(error);
              toast.error(`Unable to switch meetings to WebEx for ${course.CRN}.`);
            }),
        ),
      );
      toggleIsWebExConfirmationModalOpen();
      setCourse(null);
    } catch (err) {
      console.log(err);
      toast.error('Unable to switch meetings to WebEx for the course(s).');
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsAddCodecsModalOpen = () => {
    setIsAddCodecsModalOpen((prev) => !prev);
  };

  const onAddCodecs = (course) => {
    if (!isAddCodecsAllowed(course)) {
      toast.error(
        `The course ${course.SUBJECT_CODE} ${course.COURSE_NUMBER} - ${course.CRN} has ended. Cannot add codecs to an ended course.`,
      );
    } else {
      setCourse(course);
      toggleIsAddCodecsModalOpen();
    }
  };

  const onBulkAddCodecs = (courses = []) => {
    const { activeCourses, endedCourses } = courses.reduce(
      ({ activeCourses, endedCourses }, course) => {
        if (!isAddCodecsAllowed(course)) {
          endedCourses.push(course);
        } else {
          activeCourses.push(course);
        }
        return { activeCourses, endedCourses };
      },
      { activeCourses: [], endedCourses: [] },
    );

    if (endedCourses.length) {
      const crns = endedCourses.map((course) => course.CRN);
      toast.error(
        `The course(s) with CRN(s) ${crns.join(', ')} ${
          crns.length > 1 ? 'have' : 'has'
        } ended. Cannot add codecs to the ended course(s).`,
      );
    }
    if (activeCourses.length) {
      toggleIsAddCodecsModalOpen();
    }
  };

  // Add Codecs
  const onAddCodecsConfirm = async (codecsData) => {
    try {
      toggleIsInProgress();
      if (selectedCourse) {
        await addCodecs({
          crn: selectedCourse.CRN,
          termCode: selectedCourse.TERM_CODE,
          codecsData,
        });
        toast.success(
          `Added codecs for the course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
        );
      } else if (Object.values(selectionModel).length) {
        await bulkAddCodecs(Object.values(selectionModel), codecsData);
        toast.success('Added codecs!');
      }
      toggleIsAddCodecsModalOpen();
      setCourse(null);
      if (isDetailedCourseActionsMenu) {
        await Promise.all([loadCourse(), loadMeetings()]);
      } else if (isCourseListActionsMenu) {
        await loadCourses();
      }
    } catch (err) {
      console.log(err);
      toast.error(
        `Unable to add codecs to the course${
          selectedCourse
            ? ` ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`
            : '(s).'
        }.`,
      );
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsRemoveCodecsModalOpen = () => {
    setIsRemoveCodecsModalOpen((prev) => !prev);
  };

  const onRemoveCodecs = (course) => {
    setCourse(course);
    toggleIsRemoveCodecsModalOpen();
  };

  // Remove Codecs
  const onRemoveCodecsConfirm = async (codecsData) => {
    try {
      toggleIsInProgress();
      await removeCodecs({
        crn: selectedCourse.CRN,
        termCode: selectedCourse.TERM_CODE,
        codecs: codecsData,
      });
      toast.success(
        `Removed codecs for the course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
      toggleIsRemoveCodecsModalOpen();
      setCourse(null);
      if (isDetailedCourseActionsMenu) {
        await Promise.all([loadCourse(), loadMeetings()]);
      } else if (isCourseListActionsMenu) {
        await loadCourses();
      }
    } catch (err) {
      console.log(err);
      toast.error(
        `Unable to remove codecs for the course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsCalendarEntriesModalOpen = () => {
    setIsCalendarEntriesModalOpen((prev) => !prev);
  };

  const onCreateCalendarEntries = (course) => {
    setCourse(course);
    toggleIsCalendarEntriesModalOpen();
  };

  // Create Calendar entries
  const onCreateCalendarEntriesConfirm = async () => {
    try {
      toggleIsInProgress();
      const { failures, success } = await createCalendarEntries(selectedCourse);
      if (!failures.length) {
        toast.success(
          `Created calendar entries for ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
        );
      } else {
        if (!success.length) {
          toast.error(
            `Unable to create calendar entries for the meetings of course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
          );
        } else {
          failures.map((meetingId) => {
            toast.error(
              `Unable to create calendar entry for a meeting with ID ${meetingId} of course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
            );
          });
        }
      }
      toggleIsCalendarEntriesModalOpen();
      setCourse(null);
    } catch (err) {
      console.log(err);
      toast.error(
        `Unable to create calendar entries for ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsAdhocMeetingModalOpen = () => {
    setIsAdhocMeetingModalOpen((prev) => !prev);
  };

  const onCreateAdhocMeeting = (course) => {
    setCourse(course);
    toggleIsAdhocMeetingModalOpen();
  };

  // Create adhoc meeting
  const onCreateAdhocMeetingConfirm = async (adhocMeetingDetails) => {
    try {
      toggleIsInProgress();
      await createAdhocMeeting({
        course: selectedCourse,
        adhocMeetingDetails,
        email: getUserEmail(trueUser, impersonation),
        midas: getUserMidas(trueUser, impersonation),
      });
      toast.success(
        `Adhoc meeting created for ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
      toggleIsAdhocMeetingModalOpen();
      setCourse(null);
      if (isDetailedCourseActionsMenu) {
        await loadMeetings();
      }
    } catch (err) {
      console.log(err);
      toast.error(
        `Unable to create adhoc meeting for ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
    } finally {
      toggleIsInProgress();
    }
  };

  const toggleIsEnableGuestSpeakerModalOpen = () => {
    setIsEnableGuestSpeakerModalOpen((prev) => !prev);
  };

  const onEnableGuestSpeaker = () => {
    toggleIsEnableGuestSpeakerModalOpen();
  };

  // Enable guest speaker
  const onEnableGuestSpeakerConfirm = async () => {
    try {
      toggleIsInProgress();
      await Promise.all(
        futureMeetings.map((meeting) =>
          enableGuestSpeaker({
            crn: selectedCourse.CRN,
            termCode: selectedCourse.TERM_CODE,
            meetingId: meeting.id,
          })
            .then((result) => {
              toast.success(`Enabled guest speaker for meeting with ID ${meeting.id}.`);
            })
            .catch((error) => {
              console.log(error);
              toast.error(`Unable to enable guest speaker for meeting with ID ${meeting.id}.`);
            }),
        ),
      );
      toggleIsEnableGuestSpeakerModalOpen();
      setCourse(null);
      if (isDetailedCourseActionsMenu) {
        loadMeetings();
      }
    } catch (err) {
      console.log(err);
      toast.error(
        `Unable to enable guest speaker for the meetings of course ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}.`,
      );
    } finally {
      toggleIsInProgress();
    }
  };

  const isBulkAction = Object.keys(selectionModel).length > 0;

  return (
    <>
      <Loader id={`${id}__loader_inProgress`} isOpen={isInProgress} />

      {isClassMeetingsModalOpen && (
        <PortalConfirmation
          onToggle={() => {
            toggleIsClassMeetingsModalOpen();
            setCourse(null);
          }}
          onConfirm={onCreateClassMeetingsConfirm}
          message={`Are you sure you want to create class meetings${
            isBulkAction
              ? '?'
              : ` for ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}?`
          }`}
          title={'Create Class Meetings'}
        />
      )}

      <DeleteModal
        isOpen={isDeleteMeetingsModalOpen}
        toggleModalOpen={() => {
          toggleIsDeleteMeetingsModalOpen();
          setCourse(null);
        }}
        onConfirm={onDeleteConfirm}
        id={`${id}DeleteMeetings`}
        setPeriod={setDeleteMeetingsPeriod}
        period={deleteMeetingsPeriod}
        course={selectedCourse}
      />

      {isDeleteConfirmationModalOpen && (
        <PortalDelete
          onToggle={() => {
            toggleIsDeleteConfirmationModalOpen();
            setCourse(null);
          }}
          onDelete={onDeleteMeetings}
          message={`Are you sure you want to delete meetings${
            isBulkAction
              ? '?'
              : ` of ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}?`
          }`}
          title={'Delete Meetings'}
        />
      )}

      {isPersonalZoomLinkModalOpen && (
        <PersonalZoomLinkModal
          isOpen={isPersonalZoomLinkModalOpen}
          toggleModalOpen={() => {
            toggleIsPersonalZoomLinkModalOpen();
            setCourse(null);
          }}
          onConfirm={onSwitchToPersonalZoomLinkConfirm}
          id={`${id}PersonalZoomLink`}
          course={selectedCourse}
        />
      )}

      {isWebExConfirmationModalOpen && (
        <PortalConfirmation
          onToggle={() => {
            toggleIsWebExConfirmationModalOpen();
            setCourse(null);
          }}
          onConfirm={onSwitchToWebExConfirm}
          message={`Are you sure you want to switch meetings${
            isBulkAction
              ? ''
              : ` of ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}`
          } to WebEx?`}
          title={'Switch to WebEx Meetings'}
        />
      )}

      {isAddCodecsModalOpen && (
        <AddCodecsModal
          isOpen={isAddCodecsModalOpen}
          toggleModalOpen={() => {
            toggleIsAddCodecsModalOpen();
            setCourse(null);
          }}
          onConfirm={onAddCodecsConfirm}
          id={`${id}AddCodecs`}
          isSmall={isSmall}
          codecs={codecs}
          course={isBulkAction ? Object.values(selectionModel)[0] : selectedCourse}
          isBulkAction={isBulkAction}
        />
      )}

      {isRemoveCodecsModalOpen && (
        <RemoveCodecsModal
          isOpen={isRemoveCodecsModalOpen}
          toggleModalOpen={() => {
            toggleIsRemoveCodecsModalOpen();
            setCourse(null);
          }}
          onConfirm={onRemoveCodecsConfirm}
          id={`${id}RemoveCodecs`}
          codecs={selectedCourse.codecs}
          course={selectedCourse}
          isSmall={isSmall}
        />
      )}

      {isCalendarEntriesModalOpen && (
        <PortalConfirmation
          onToggle={() => {
            toggleIsCalendarEntriesModalOpen();
            setCourse(null);
          }}
          onConfirm={onCreateCalendarEntriesConfirm}
          message={`Are you sure you want to create calendar entries for the meetings of ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}?`}
          title={'Create Calendar Entries'}
        />
      )}

      {isAdhocMeetingModalOpen && (
        <AdhocMeetingModal
          isOpen={isAdhocMeetingModalOpen}
          toggleModalOpen={() => {
            toggleIsAdhocMeetingModalOpen();
            setCourse(null);
          }}
          onConfirm={onCreateAdhocMeetingConfirm}
          id={`${id}AdhocMeeting`}
          course={selectedCourse}
          codecs={codecs}
          isSmall={isSmall}
        />
      )}

      {isEnableGuestSpeakerModalOpen && (
        <PortalConfirmation
          onToggle={() => {
            toggleIsEnableGuestSpeakerModalOpen();
            setCourse(null);
          }}
          onConfirm={onEnableGuestSpeakerConfirm}
          message={`Are you sure you want to enable guest speaker for all the meetings of ${selectedCourse.SUBJECT_CODE} ${selectedCourse.COURSE_NUMBER} - ${selectedCourse.CRN}?`}
          title={'Enable Guest Speaker'}
        />
      )}
    </>
  );
}
